/**
 * Rule-based discovery engine behind the homepage industry explorer and the
 * /find-opportunities diagnostic. Deterministic on purpose: every figure it
 * produces is labelled illustrative and traceable to an input.
 *
 * The customer chooses the problem. The engine chooses the technology.
 */

export const GOALS = [
  { id: "makeMore", label: "Make more", sub: "More leads, better conversion, repeat customers" },
  { id: "loseLess", label: "Lose less", sub: "Missed leads, no-shows, overdue payments, waste" },
  { id: "workLess", label: "Work less", sub: "Data entry, follow-ups, reporting, scheduling" },
  { id: "knowMore", label: "Know more", sub: "Calls, cash, operations, alerts before you ask" },
];

export const CHANNELS = [
  { id: "phone", label: "Phone" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "website", label: "Website" },
  { id: "walkin", label: "Walk-ins" },
  { id: "email", label: "Email" },
  { id: "salesteam", label: "Sales team" },
  { id: "multiple", label: "Several of these" },
];

export const TIME_SINKS = [
  { id: "followups", label: "Follow-ups" },
  { id: "dataentry", label: "Data entry" },
  { id: "calls", label: "Calls" },
  { id: "scheduling", label: "Scheduling" },
  { id: "paperwork", label: "Paperwork" },
  { id: "reporting", label: "Reporting" },
  { id: "collections", label: "Collections" },
  { id: "support", label: "Customer support" },
  { id: "leads", label: "Lead management" },
  { id: "other", label: "Something else" },
];

export const SYSTEMS = [
  { id: "crm", label: "CRM" },
  { id: "excel", label: "Excel / Sheets" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "tally", label: "Tally / accounting" },
  { id: "email", label: "Email" },
  { id: "erp", label: "ERP" },
  { id: "paper", label: "Paper" },
  { id: "multiple", label: "Several systems" },
];

/* ------------------------------------------------------------------------
   The opportunity catalogue. `problem` is what the owner recognises;
   `builds` is what AUT labs would put in place. Triggers score the match.
   ------------------------------------------------------------------------ */
export const OPPORTUNITIES = {
  missedCalls: {
    problem: "Missed calls",
    title: "Answer every call, even when the team is busy",
    category: "loseLess",
    builds: ["AI voice agent", "Call-back scheduling", "WhatsApp confirmation", "CRM lead capture"],
    triggers: { timeSinks: ["calls", "leads"], channels: ["phone", "walkin"], goals: ["loseLess", "makeMore"] },
  },
  followUps: {
    problem: "Manual follow-ups",
    title: "Follow up every lead without anyone remembering to",
    category: "makeMore",
    builds: ["Automated reminders", "AI-drafted follow-ups", "CRM task creation", "Escalation when a lead goes quiet"],
    triggers: { timeSinks: ["followups", "leads"], channels: ["salesteam", "whatsapp", "website"], goals: ["makeMore", "workLess"] },
  },
  leadMgmt: {
    problem: "Leads scattered across people and apps",
    title: "One place where every enquiry lands and moves",
    category: "makeMore",
    builds: ["Lead capture from every channel", "CRM automation", "Lead prioritisation", "Owner view of the pipeline"],
    triggers: { timeSinks: ["leads", "followups"], channels: ["multiple", "website", "whatsapp"], systems: ["excel", "whatsapp", "paper", "multiple"], goals: ["makeMore", "knowMore"] },
  },
  whatsappAI: {
    problem: "Enquiries waiting on WhatsApp",
    title: "Reply to enquiries in seconds, hand over the hard ones",
    category: "workLess",
    builds: ["WhatsApp assistant", "Qualification questions", "Handover to a person", "Every chat logged to CRM"],
    triggers: { timeSinks: ["support", "leads", "followups"], channels: ["whatsapp"], systems: ["whatsapp"], goals: ["workLess", "makeMore"] },
  },
  noShows: {
    problem: "No-shows and double bookings",
    title: "Confirm, remind and reschedule without a phone call",
    category: "loseLess",
    builds: ["Booking against real availability", "Reminder automation", "Rescheduling by WhatsApp", "Calendar sync"],
    triggers: { timeSinks: ["scheduling", "calls"], channels: ["phone", "whatsapp", "walkin"], goals: ["loseLess", "workLess"] },
  },
  callIntel: {
    problem: "Sales calls nobody can review",
    title: "Turn every sales call into structured business data",
    category: "knowMore",
    builds: ["Call transcription", "Objection detection", "CRM updates from the call", "Weekly sales insights"],
    triggers: { timeSinks: ["calls", "leads", "reporting"], channels: ["phone", "salesteam"], goals: ["knowMore", "makeMore"] },
  },
  documents: {
    problem: "Retyping documents",
    title: "Read invoices, orders and forms so nobody retypes them",
    category: "workLess",
    builds: ["Document extraction", "Validation against your records", "Approval for anything unusual", "Posting into Tally or ERP"],
    triggers: { timeSinks: ["dataentry", "paperwork"], systems: ["paper", "excel", "tally", "erp"], goals: ["workLess", "loseLess"] },
  },
  collections: {
    problem: "Overdue payments",
    title: "Get paid on time without awkward calls",
    category: "loseLess",
    builds: ["Ageing tracked automatically", "Reminders in your tone", "Escalation rules", "Weekly cash view"],
    triggers: { timeSinks: ["collections"], systems: ["tally", "excel"], goals: ["loseLess", "knowMore"] },
  },
  reporting: {
    problem: "Reports built by hand",
    title: "See what needs your attention before someone tells you",
    category: "knowMore",
    builds: ["Daily owner brief", "Exception alerts", "Management dashboard", "Anomaly detection"],
    triggers: { timeSinks: ["reporting"], systems: ["excel", "multiple", "erp"], goals: ["knowMore"] },
  },
  inventory: {
    problem: "Stock and purchase surprises",
    title: "Know what to reorder before you run out",
    category: "loseLess",
    builds: ["Low-stock alerts", "Purchase-order extraction", "Vendor follow-up", "Dead-stock detection"],
    triggers: { timeSinks: ["paperwork", "dataentry"], systems: ["erp", "tally", "excel"], goals: ["loseLess", "knowMore"] },
  },
  vendorComms: {
    problem: "Chasing vendors and partners",
    title: "Keep vendor conversations moving on their own",
    category: "workLess",
    builds: ["Order status workflows", "Automated vendor reminders", "Document collection", "Exception routing"],
    triggers: { timeSinks: ["followups", "paperwork"], systems: ["email", "whatsapp"], goals: ["workLess"] },
  },
  reviews: {
    problem: "Reviews and repeat visits left to chance",
    title: "Ask for the review and the next visit at the right moment",
    category: "makeMore",
    builds: ["Post-visit follow-up", "Review requests", "Recall reminders", "Feedback routing"],
    triggers: { timeSinks: ["followups"], channels: ["walkin", "phone"], goals: ["makeMore"] },
  },
  support: {
    problem: "Same questions answered from memory",
    title: "Answer routine questions instantly, log every promise",
    category: "workLess",
    builds: ["Support assistant", "FAQ automation", "Ticket routing", "Escalation to a person"],
    triggers: { timeSinks: ["support"], channels: ["whatsapp", "email", "phone"], goals: ["workLess"] },
  },
  renewals: {
    problem: "Contracts and renewals slipping",
    title: "Catch every renewal before it expires",
    category: "loseLess",
    builds: ["Renewal tracking", "Reminders before expiry", "Lapsed-contract detection", "Recovery workflow"],
    triggers: { timeSinks: ["followups", "collections"], systems: ["excel", "paper"], goals: ["loseLess", "makeMore"] },
  },
  approvals: {
    problem: "Approvals stuck in someone's inbox",
    title: "Route approvals to the right person and chase them for you",
    category: "workLess",
    builds: ["Approval routing", "Escalation reminders", "Audit trail", "Internal helpdesk"],
    triggers: { timeSinks: ["paperwork", "other"], systems: ["email", "multiple"], goals: ["workLess", "knowMore"] },
  },
};

/* ------------------------------------------------------------------------
   Industries. `flow` is the six-step workflow shown in the explorer,
   `defaults` the opportunities most often relevant, `signals` the pattern
   we typically see (illustrative, never a client figure).
   ------------------------------------------------------------------------ */
export const INDUSTRIES = [
  {
    id: "dentist", label: "Dentist / clinic", short: "Dentist",
    flow: ["Phone rings", "AI answers", "Appointment booked", "Reminder sent", "Patient visits", "Review requested"],
    defaults: ["missedCalls", "noShows", "whatsappAI", "reviews", "documents"],
    signals: [["Calls missed while treating", "Every one is a patient"], ["No-shows", "Empty chair, paid staff"], ["Front desk time on reminders", "Hours a week"]],
  },
  {
    id: "realestate", label: "Real estate", short: "Real estate",
    flow: ["Lead arrives", "AI qualification", "Call intelligence", "CRM updated", "Site visit booked", "Follow-up on time"],
    defaults: ["followUps", "callIntel", "leadMgmt", "noShows", "missedCalls"],
    signals: [["Lead response time", "Minutes decide the deal"], ["Dormant leads", "Most never hear back"], ["Site-visit no-shows", "Weekends lost"]],
  },
  {
    id: "manufacturing", label: "Manufacturing", short: "Manufacturing",
    flow: ["Purchase order arrives", "Document extraction", "Validation", "Approval", "ERP updated", "Inventory adjusted"],
    defaults: ["documents", "inventory", "vendorComms", "reporting", "approvals"],
    signals: [["Orders retyped", "Every one, by hand"], ["Stock surprises", "Cash tied up"], ["Production reporting", "Rebuilt weekly"]],
  },
  {
    id: "restaurant", label: "Restaurant / cafe", short: "Restaurant",
    flow: ["Reservation request", "AI confirms", "Reminder sent", "Guest arrives", "Feedback captured", "Repeat visit prompted"],
    defaults: ["missedCalls", "noShows", "reviews", "inventory", "reporting"],
    signals: [["Calls missed in service", "Tables lost"], ["No-shows on weekends", "Prime covers"], ["Daily numbers", "Known next morning"]],
  },
  {
    id: "logistics", label: "Logistics / transport", short: "Logistics",
    flow: ["Booking request", "Auto-confirmation", "Dispatch scheduled", "Live status", "Proof of delivery", "Invoice raised"],
    defaults: ["support", "documents", "collections", "vendorComms", "reporting"],
    signals: [["Where is my shipment?", "Answered by phone"], ["POD paperwork", "Days to invoice"], ["Receivables", "Chased by memory"]],
  },
  {
    id: "professional", label: "CA, law or consulting", short: "Professional services",
    flow: ["Client enquiry", "Intake captured", "Documents collected", "Work assigned", "Approval", "Invoice and follow-up"],
    defaults: ["documents", "followUps", "collections", "approvals", "reporting"],
    signals: [["Client documents", "Chased by WhatsApp"], ["Deadlines", "Tracked in heads"], ["Billing", "Delayed by admin"]],
  },
  {
    id: "distribution", label: "Distribution / trading", short: "Distribution",
    flow: ["Order on WhatsApp", "Order captured", "Stock checked", "Dispatch", "Invoice posted", "Payment reminder"],
    defaults: ["whatsappAI", "documents", "inventory", "collections", "reporting"],
    signals: [["Orders arrive as messages", "Retyped into Tally"], ["Stock-outs", "Found by the customer"], ["Outstanding payments", "Chased late"]],
  },
  {
    id: "fieldservice", label: "Field service / AMC", short: "Field service",
    flow: ["Service call", "Ticket created", "Engineer scheduled", "Visit reminder", "Report submitted", "Renewal tracked"],
    defaults: ["renewals", "noShows", "missedCalls", "documents", "collections"],
    signals: [["Contract renewals", "Missed quietly"], ["Engineer scheduling", "By phone, daily"], ["Service reports", "On paper"]],
  },
  {
    id: "hotel", label: "Hotel / hospitality", short: "Hotel",
    flow: ["Booking enquiry", "AI responds", "Booking confirmed", "Pre-arrival message", "Stay", "Review and rebooking"],
    defaults: ["whatsappAI", "missedCalls", "reviews", "reporting", "support"],
    signals: [["Enquiries at night", "Answered next morning"], ["Guest questions", "Same ten, all day"], ["Occupancy view", "Built by hand"]],
  },
  {
    id: "school", label: "School / coaching", short: "Education",
    flow: ["Admission enquiry", "AI qualification", "Counsellor call", "Documents collected", "Fee reminder", "Parent update"],
    defaults: ["followUps", "whatsappAI", "collections", "documents", "reporting"],
    signals: [["Admission enquiries", "Lost between seasons"], ["Fee follow-up", "Manual, awkward"], ["Parent updates", "Bulk messages"]],
  },
  {
    id: "recruitment", label: "Recruitment / staffing", short: "Recruitment",
    flow: ["Candidate applies", "AI screening", "Interview scheduled", "Reminder sent", "Feedback captured", "Client updated"],
    defaults: ["followUps", "noShows", "documents", "callIntel", "reporting"],
    signals: [["Interview no-shows", "Recruiter time lost"], ["Candidate follow-up", "Falls through"], ["Client reporting", "Weekly scramble"]],
  },
  {
    id: "ecommerce", label: "E-commerce / D2C", short: "E-commerce",
    flow: ["Order placed", "Confirmation sent", "Fulfilment", "Where is my order?", "Delivery", "Review and reorder"],
    defaults: ["support", "whatsappAI", "reviews", "inventory", "reporting"],
    signals: [["Order status questions", "Most of support"], ["Returns and refunds", "Manual"], ["Repeat purchase", "Left to chance"]],
  },
  {
    id: "other", label: "Something else", short: "Other",
    flow: ["Work arrives", "Captured", "Checked against your rules", "Person approves", "System acts", "Owner sees the result"],
    defaults: ["leadMgmt", "followUps", "documents", "reporting", "approvals"],
    signals: [["Every business", "Has a different leak"], ["We start", "With how yours works"], ["Then choose", "The technology"]],
  },
];

/* The seven industries the homepage explorer shows. */
export const EXPLORER_IDS = ["dentist", "realestate", "manufacturing", "restaurant", "logistics", "professional", "other"];

export const industryById = (id) => INDUSTRIES.find((i) => i.id === id) || INDUSTRIES[INDUSTRIES.length - 1];

const has = (list = [], id) => list.includes(id);

/**
 * Scores the catalogue against the answers. Industry defaults seed the list,
 * so an owner who picks an industry and nothing else still gets a sensible map.
 */
export function scoreOpportunities(answers) {
  const ind = industryById(answers.industry);
  const scored = Object.entries(OPPORTUNITIES).map(([id, o]) => {
    let score = 0;
    const d = ind.defaults.indexOf(id);
    if (d >= 0) score += 3 - d * 0.5;
    const t = o.triggers;
    (t.timeSinks || []).forEach((x) => { if (has(answers.timeSinks, x)) score += 2; });
    (t.channels || []).forEach((x) => { if (has(answers.channels, x)) score += 1.2; });
    (t.systems || []).forEach((x) => { if (has(answers.systems, x)) score += 0.8; });
    if (answers.goal && (t.goals || []).includes(answers.goal)) score += o.category === answers.goal ? 2.5 : 1.2;
    return { id, ...o, score };
  });
  return scored
    .filter((o) => o.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map((o) => ({ ...o, impact: o.score >= 6.5 ? "High" : o.score >= 4 ? "Medium to high" : "Medium" }));
}

/**
 * Revenue gap. Assumption, stated on screen: leads that never got a follow-up
 * did not convert. If follow-up coverage rises to 95%, conversion rises in the
 * same proportion, capped at 1.5x the current rate.
 */
export function estimateRevenue({ leads, conversion, value, followUp }) {
  const L = Math.max(0, Number(leads) || 0);
  const c = Math.min(100, Math.max(0, Number(conversion) || 0)) / 100;
  const V = Math.max(0, Number(value) || 0);
  const f = Math.min(100, Math.max(1, Number(followUp) || 1)) / 100;
  const target = Math.max(f, 0.95);
  const improved = Math.min(c * 1.5, c * (target / f));
  const current = Math.round(L * c * V);
  const withImprovement = Math.round(L * improved * V);
  return { current, withImprovement, gap: withImprovement - current, targetFollowUp: Math.round(target * 100), improvedConversion: +(improved * 100).toFixed(1) };
}

/** Hours. Assumption: about half of a repetitive job can be automated. */
export function estimateHours({ hoursPerWeek }, share = 0.5) {
  const h = Math.max(0, Number(hoursPerWeek) || 0);
  const weekly = h * share;
  return { weekly: +weekly.toFixed(1), yearly: Math.round(weekly * 48), share: Math.round(share * 100) };
}

export const formatINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, "")} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1).replace(/\.0$/, "")} L`;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

/** Four phases, personalised from the answers and the scored opportunities. */
export function buildRoadmap(answers, opps) {
  const systems = (answers.systems || []).filter((s) => s !== "multiple").map((s) => SYSTEMS.find((x) => x.id === s)?.label).filter(Boolean);
  const sys = systems.length ? systems.slice(0, 3).join(", ") : "the tools you already use";
  const first = opps[0];
  const workLess = opps.find((o) => o.category === "workLess" || o.category === "loseLess") || first;
  const intel = opps.find((o) => o.category === "knowMore") || OPPORTUNITIES.reporting;
  const goal = GOALS.find((g) => g.id === answers.goal) || GOALS[3];
  return [
    { n: "01", title: "Connect your existing systems", input: sys, system: "AUT labs data layer", action: "Every enquiry, document and message captured in one place", outcome: first ? `Nothing about "${first.problem.toLowerCase()}" goes unseen` : "Nothing goes unseen" },
    { n: "02", title: "Automate repetitive workflows", input: workLess?.problem || "Repetitive work", system: workLess?.builds?.[0] || "Workflow automation", action: workLess?.builds?.slice(1, 3).join(" and ") || "Reminders and routing", outcome: "Your team stops doing it by hand" },
    { n: "03", title: "Add AI intelligence", input: intel.problem, system: intel.builds[0], action: intel.builds.slice(1, 3).join(" and "), outcome: "Decisions made with the full picture" },
    { n: "04", title: "Measure and optimise", input: "What the system did each week", system: "Owner brief and alerts", action: `Track the ${goal.label.toLowerCase()} numbers you chose`, outcome: "You see the return, and what to fix next" },
  ];
}
